/**
 * Render a free-text note comment as safe HTML for `v-html`.
 *
 * Notes can include light structural HTML (Vern wraps lines in `<p>`, others use
 * `<br>`) plus HTML entities (`&nbsp;`, `&amp;`, numeric refs like `&#039;`).
 *
 * Strategy: escape `<` and `>` only — which neutralises ALL tags and attributes
 * — then restore the few safe structural tags (`<p>`, `</p>`, `<br>`). `&` is
 * deliberately left unescaped so existing entities decode in the browser; with
 * angle brackets gone, a stray `&` can't be used to inject a tag or attribute.
 *
 * Everything else (script/img/onerror, and even `<p onclick=…>` — note the
 * restore only revives the BARE tags) stays escaped and renders as inert text.
 *
 * @param {string|null|undefined} raw
 * @returns {string} sanitised HTML safe to pass to v-html
 */
export function sanitiseNoteComment(raw) {
    const escaped = (raw ?? '').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    return escaped
        .replace(/&lt;p&gt;/gi, '<p>')
        .replace(/&lt;\/p&gt;/gi, '</p>')
        .replace(/&lt;br\s*\/?&gt;/gi, '<br>');
}

/**
 * Sanitised note HTML with each @mention picked out.
 *
 * The names are escaped the same way {@see sanitiseNoteComment} escapes the body
 * before they are matched - the sanitiser has already turned angle brackets into
 * entities, so a name containing one would not be found in the sanitised html
 * otherwise.
 *
 * @param {{comment?: string|null, mentioned_users?: Array<{fullname?: string|null}>}} note
 * @returns {string} sanitised HTML safe to pass to v-html
 */
export function highlightNoteMentions(note) {
    const escapeAngles = (value) =>
        String(value ?? '')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');

    let html = sanitiseNoteComment(note?.comment);

    (note?.mentioned_users ?? []).forEach((user) => {
        const mention = escapeAngles(`@${user.fullname}`);
        const pattern = mention.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        html = html.replace(new RegExp(pattern, 'g'), `<span class="font-medium text-brand">${mention}</span>`);
    });

    return html;
}
